import * as duckdb from '@duckdb/duckdb-wasm';
import * as XLSX from 'xlsx';
import datasetStorage from './datasetStorage';

const sanitizeSQL = (sql, columns = []) => {
  let sanitizedSQL = sql;
  
  columns.forEach(column => {
    if (column.includes(' ') || column.includes('-') || column.includes('.')) {
      const unquotedPattern = new RegExp(`\\b${column.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      const quotedColumn = `"${column}"`;
      
      sanitizedSQL = sanitizedSQL.replace(unquotedPattern, (match, offset) => {
        const beforeChar = sanitizedSQL.charAt(offset - 1);
        const afterChar = sanitizedSQL.charAt(offset + match.length);
        if (beforeChar === '"' || afterChar === '"') {
          return match;
        }
        return quotedColumn;
      });
    }
  });
  
  const dateComparisonPattern = /WHERE\s+(\w+|"\w+[^"]*")\s*([><=!]+)\s*'(\d{4}-\d{2}-\d{2})'/gi;
  sanitizedSQL = sanitizedSQL.replace(dateComparisonPattern, (match, column, operator, dateValue) => {
    const year = dateValue.substring(0, 4);
    const columnName = column.replace(/"/g, '').toLowerCase();
    if (columnName.includes('year') || columnName.includes('founded') || columnName.includes('established')) {
      return `WHERE ${column} ${operator} ${year}`;
    }
    return match;
  });
  
  const yearColumnPattern = /(\w*(?:year|founded|established|created|born)\w*|"\w*(?:year|founded|established|created|born)\w*")\s*([><=!]+)\s*'(\d{4})-\d{2}-\d{2}'/gi;
  sanitizedSQL = sanitizedSQL.replace(yearColumnPattern, (match, column, operator, year) => {
    return `${column} ${operator} ${year}`;
  });
  
  return sanitizedSQL;
};

const formatSQLError = (error, sql) => {
  let message = error.message;
  
  if (message.includes('syntax error at or near')) {
    message += '\n\nTip: Column names with spaces should be wrapped in double quotes (e.g., "Last Name")';
  }
  
  if (message.includes('column') && message.includes('does not exist')) {
    message += '\n\nTip: Check your column names. Use the schema view to see available columns.';
  }
  
  if (message.includes('Parser Error')) {
    message += '\n\nTip: Check your SQL syntax. Try using the sample queries as a reference.';
  }
  
  if (message.includes('Conversion Error')) {
    if (message.includes('Could not convert string') && message.includes('to INT64')) {
      const stringMatch = message.match(/string '([^']+)'/);
      const lineMatch = message.match(/LINE \d+: (.+)/);
      
      if (stringMatch && lineMatch) {
        const problematicValue = stringMatch[1];
        const sqlLine = lineMatch[1];
        
        if (problematicValue.match(/^\d{4}-\d{2}-\d{2}$/) && sqlLine.includes('WHERE')) {
          message += `\n\nTip: It looks like you're comparing a date '${problematicValue}' with a numeric year column. Try using just the year instead (e.g., WHERE column_name > 2011).`;
        } else if (problematicValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          message += `\n\nTip: The column appears to contain integers, but you're using a date format. Try extracting the year: ${problematicValue.substring(0, 4)}`;
        } else {
          message += '\n\nTip: Check the data types - you may be comparing incompatible types (string vs number).';
        }
      }
    } else if (message.includes('Could not convert') && message.includes('to')) {
      message += '\n\nTip: Data type mismatch detected. Check if you\'re comparing the right data types (text vs numbers vs dates).';
    }
  }
  
  if (message.includes('function') && message.includes('does not exist')) {
    message += '\n\nTip: Check your function names. DuckDB may use different function names than other SQL databases.';
  }
  
  return message;
};

// Helper function to convert BigInt values to regular numbers or strings
const convertBigIntValues = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj === 'bigint') {
    // Convert BigInt to number if it fits in safe integer range, otherwise to string
    return obj <= Number.MAX_SAFE_INTEGER && obj >= Number.MIN_SAFE_INTEGER 
      ? Number(obj) 
      : obj.toString();
  }
  
  if (Array.isArray(obj)) {
    return obj.map(convertBigIntValues);
  }
  
  if (typeof obj === 'object') {
    const converted = {};
    for (const [key, value] of Object.entries(obj)) {
      converted[key] = convertBigIntValues(value);
    }
    return converted;
  }
  
  return obj;
};

class DuckDBService {
  constructor() {
    this.db = null;
    this.conn = null;
    this.initialized = false;
    this.datasets = new Map();
    this.persistedLoaded = false;
  }

  async initialize() {
    if (this.initialized) return;

    try {
      const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
      
      const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);

      const worker_url = URL.createObjectURL(
        new Blob([`importScripts("${bundle.mainWorker}");`], {type: 'text/javascript'})
      );

      const worker = new Worker(worker_url);
      const logger = new duckdb.ConsoleLogger();
      this.db = new duckdb.AsyncDuckDB(logger, worker);
      await this.db.instantiate(bundle.mainModule, bundle.pthreadWorker);
      URL.revokeObjectURL(worker_url);

      this.conn = await this.db.connect();
      this.initialized = true;
      
      console.log('DuckDB-Wasm initialized successfully');
    } catch (error) {
      console.error('Failed to initialize DuckDB-Wasm:', error);
      throw error;
    }
  }

  async restorePersistedDatasets() {
    if (this.persistedLoaded) return;
    this.persistedLoaded = true;

    if (!datasetStorage.isSupported()) {
      return;
    }

    try {
      await this.initialize();
      const records = await datasetStorage.loadAllDatasets();
      await this.loadPersistedRecords(records);
    } catch (error) {
      console.warn('Failed to load persisted datasets:', error);
    }
  }

  async loadPersistedRecords(records = []) {
    for (const record of records) {
      if (!record?.file_id || this.datasets.has(record.file_id)) {
        continue;
      }

      const tableName = `user_data_${record.file_id.replace(/-/g, '_')}`;
      try {
        await this.createTableFromRecord(record, tableName);
        this.datasets.set(record.file_id, {
          tableName,
          columns: record.columns || [],
          rowCount: Number(record.row_count || 0),
          filename: record.filename,
          upload_time: record.upload_time,
          created_at: record.created_at,
          file_size_bytes: Number(record.file_size_bytes || 0)
        });
      } catch (error) {
        console.error(`Failed to restore dataset ${record.file_id}:`, error);
      }
    }
  }

  async createTableFromRecord(record, tableName) {
    const fileExtension = (record.file_extension || '').toLowerCase();
    const filePath = `${record.file_id}.${fileExtension || 'data'}`;

    await this.conn.query(`DROP TABLE IF EXISTS ${tableName}`);

    switch (fileExtension) {
      case 'csv': {
        const text = record.data || '';
        await this.db.registerFileText(filePath, text);
        await this.conn.query(`CREATE TABLE ${tableName} AS SELECT * FROM read_csv_auto('${filePath}')`);
        break;
      }
      case 'json': {
        let text = record.data || '';
        let jsonContent;

        try {
          jsonContent = JSON.parse(text);
        } catch (error) {
          if (String(error.message || '').includes('Unexpected non-whitespace character')) {
            const lines = text.trim().split('\n');
            const jsonlContent = lines.filter(line => line.trim()).map(line => JSON.parse(line));
            text = JSON.stringify(jsonlContent);
            jsonContent = jsonlContent;
          } else {
            throw error;
          }
        }

        await this.db.registerFileText(filePath, text);

        try {
          await this.conn.query(`CREATE TABLE ${tableName} AS SELECT * FROM read_json_auto('${filePath}')`);
        } catch (error) {
          if (String(error.message || '').includes('unnamed struct')) {
            const firstRow = Array.isArray(jsonContent) ? jsonContent[0] : jsonContent;
            const columns = Object.keys(firstRow || {}).map(key => {
              const value = firstRow[key];
              const type = typeof value === 'number' ? 'DOUBLE'
                : typeof value === 'boolean' ? 'BOOLEAN'
                : value === null ? 'VARCHAR'
                : 'VARCHAR';
              return `"${key}" ${type}`;
            }).join(', ');

            await this.conn.query(`
              CREATE TABLE ${tableName} (${columns});
              INSERT INTO ${tableName}
              SELECT * FROM read_json('${filePath}', columns=(${columns}));
            `);
          } else {
            throw error;
          }
        }
        break;
      }
      case 'xlsx':
      case 'xls': {
        const arrayBuffer = record.data;
        const workbook = new Uint8Array(arrayBuffer || []);
        const csvContent = await this.convertExcelToCSV(workbook);
        const csvFilePath = `${record.file_id}.csv`;
        await this.db.registerFileText(csvFilePath, csvContent);
        await this.conn.query(`
          CREATE TABLE ${tableName} AS 
          SELECT * FROM read_csv_auto('${csvFilePath}', 
            header=true,
            delim=',',
            quote='"',
            escape='"',
            nullstr='',
            ignore_errors=true,
            all_varchar=true,
            sample_size=1000
          )
        `);
        break;
      }
      case 'parquet': {
        const parquetBuffer = record.data || new ArrayBuffer(0);
        await this.db.registerFileBuffer(filePath, new Uint8Array(parquetBuffer));
        await this.conn.query(`CREATE TABLE ${tableName} AS SELECT * FROM read_parquet('${filePath}')`);
        break;
      }
      default:
        throw new Error(`Unsupported persisted file type: ${fileExtension}`);
    }
  }

  async uploadFile(file, fileId) {
    if (!this.initialized) {
      await this.initialize();
    }

    try {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const tableName = `user_data_${fileId.replace(/-/g, '_')}`;
      let persistedData = null;
      let persistedDataType = null;
      
      let text;
      let filePath;
      
      switch (fileExtension) {
        case 'csv':
          text = await file.text();
          persistedData = text;
          persistedDataType = 'text';
          filePath = `${fileId}.csv`;
          await this.db.registerFileText(filePath, text);
          await this.conn.query(`CREATE TABLE ${tableName} AS SELECT * FROM read_csv_auto('${filePath}')`);
          break;
          
        case 'json':
          text = await file.text();
          persistedData = text;
          persistedDataType = 'text';
          filePath = `${fileId}.json`;
          
          const lines = text.trim().split('\n');
          let jsonContent;
          
          try {
            jsonContent = JSON.parse(text);
          } catch (error) {
            if (error.message.includes('Unexpected non-whitespace character')) {
              try {
                const jsonlContent = lines
                  .filter(line => line.trim())
                  .map(line => JSON.parse(line));
                text = JSON.stringify(jsonlContent);
                persistedData = text;
                await this.db.registerFileText(filePath, text);
                jsonContent = jsonlContent;
              } catch (jsonlError) {
                throw new Error('Invalid JSON format: File must be either a valid JSON object/array or JSONL format (one JSON object per line)');
              }
            } else {
              throw error;
            }
          }
          
          await this.db.registerFileText(filePath, text);
          
          try {
            await this.conn.query(`CREATE TABLE ${tableName} AS SELECT * FROM read_json_auto('${filePath}')`);
          } catch (error) {
            if (error.message.includes('unnamed struct')) {
              const firstRow = Array.isArray(jsonContent) ? jsonContent[0] : jsonContent;
              const columns = Object.keys(firstRow).map(key => {
                const value = firstRow[key];
                const type = typeof value === 'number' ? 'DOUBLE' :
                           typeof value === 'boolean' ? 'BOOLEAN' :
                           value === null ? 'VARCHAR' :
                           'VARCHAR';
                return `"${key}" ${type}`;
              }).join(', ');
              
              await this.conn.query(`
                CREATE TABLE ${tableName} (${columns});
                INSERT INTO ${tableName}
                SELECT * FROM read_json('${filePath}', columns=(${columns}));
              `);
            } else {
              throw error;
            }
          }
          break;
          
        case 'xlsx':
        case 'xls':
          const arrayBuffer = await file.arrayBuffer();
          persistedData = arrayBuffer;
          persistedDataType = 'binary';
          try {
            const workbook = new Uint8Array(arrayBuffer);
            const csvContent = await this.convertExcelToCSV(workbook);
            
            const csvFilePath = `${fileId}.csv`;
            await this.db.registerFileText(csvFilePath, csvContent);
            
            await this.conn.query(`
              CREATE TABLE ${tableName} AS 
              SELECT * FROM read_csv_auto('${csvFilePath}', 
                header=true,
                delim=',',
                quote='"',
                escape='"',
                nullstr='',
                ignore_errors=true,
                all_varchar=true,
                sample_size=1000
              )
            `);
            
            const countResult = await this.conn.query(`SELECT COUNT(*) as count FROM ${tableName}`);
            const count = countResult.toArray()[0].count;
            
            if (count === 0) {
              throw new Error('No data found in the Excel file');
            }
          } catch (error) {
            console.error('Excel processing error:', error);
            throw new Error('Failed to process Excel file. Please ensure the file is a valid Excel file and contains data.');
          }
          break;
          
        case 'parquet':
          const parquetBuffer = await file.arrayBuffer();
          persistedData = parquetBuffer;
          persistedDataType = 'binary';
          filePath = `${fileId}.parquet`;
          await this.db.registerFileBuffer(filePath, new Uint8Array(parquetBuffer));
          await this.conn.query(`CREATE TABLE ${tableName} AS SELECT * FROM read_parquet('${filePath}')`);
          break;
          
        default:
          throw new Error(`Unsupported file type: ${fileExtension}`);
      }
      
      const result = await this.conn.query(`SELECT * FROM ${tableName} LIMIT 5`);
      const columns = result.schema.fields.map(field => field.name);
      
      const countResult = await this.conn.query(`SELECT COUNT(*) as count FROM ${tableName}`);
      const countData = convertBigIntValues(countResult.toArray());
      const rowCount = Number(countData[0].count);
      
      const fileSizeBytes = Number(file.size || 0);
      const uploadTimeSeconds = Math.floor(Date.now() / 1000);
      const createdAtIso = new Date(uploadTimeSeconds * 1000).toISOString();

      this.datasets.set(fileId, {
        tableName,
        columns,
        rowCount,
        filename: file.name,
        upload_time: uploadTimeSeconds,
        created_at: createdAtIso,
        file_size_bytes: fileSizeBytes
      });

      const previewData = convertBigIntValues(result.toArray());
      
      try {
        await datasetStorage.saveDataset({
          file_id: fileId,
          filename: file.name,
          file_extension: fileExtension,
          data_type: persistedDataType,
          data: persistedData,
          columns,
          row_count: rowCount,
          file_size_bytes: fileSizeBytes,
          upload_time: uploadTimeSeconds,
          created_at: createdAtIso
        });
      } catch (error) {
        console.warn('Failed to persist dataset:', error);
      }

      return {
        file_id: fileId,
        filename: file.name,
        columns,
        row_count: rowCount,
        preview: previewData,
        file_size_bytes: fileSizeBytes,
        upload_time: uploadTimeSeconds,
        created_at: createdAtIso
      };
    } catch (error) {
      console.error('Error uploading file to DuckDB:', error);
      throw error;
    }
  }

  async executeQuery(sql, fileId) {
    if (!this.initialized) {
      await this.initialize();
    }

    if (!this.datasets.has(fileId)) {
      throw new Error('Dataset not found. Please upload a file first.');
    }

    const dataset = this.datasets.get(fileId);
    
    try {
      let sanitizedSQL = sanitizeSQL(sql, dataset.columns);
      
      const hardcodedTablePattern = /\buser_data\b(?!_)/gi;
      sanitizedSQL = sanitizedSQL.replace(hardcodedTablePattern, dataset.tableName);
      
      const startTime = performance.now();
      const result = await this.conn.query(sanitizedSQL);
      const executionTime = performance.now() - startTime;
      
      const resultArray = result.toArray();
      const convertedResult = convertBigIntValues(resultArray);
      
      return {
        sql: sanitizedSQL,
        original_sql: sql,
        result: convertedResult,
        row_count: convertedResult.length,
        execution_time_ms: Math.round(executionTime * 100) / 100
      };
    } catch (error) {
      console.error('Error executing query:', error);
      const formattedError = new Error(formatSQLError(error, sql));
      formattedError.originalError = error;
      formattedError.sql = sql;
      throw formattedError;
    }
  }

  async viewData(fileId, limit = 100, offset = 0) {
    if (!this.initialized) {
      await this.initialize();
    }

    if (!this.datasets.has(fileId)) {
      throw new Error('Dataset not found');
    }

    const dataset = this.datasets.get(fileId);
    
    try {
      const sql = `SELECT * FROM ${dataset.tableName} LIMIT ${limit} OFFSET ${offset}`;
      const result = await this.conn.query(sql);
      const data = result.toArray();
      const convertedData = convertBigIntValues(data);
      
      return {
        file_id: fileId,
        filename: dataset.filename,
        total_rows: dataset.rowCount,
        showing_rows: convertedData.length,
        offset,
        limit,
        columns: dataset.columns,
        data: convertedData,
        column_types: {}
      };
    } catch (error) {
      console.error('Error viewing data:', error);
      throw error;
    }
  }

  getDatasetInfo(fileId) {
    return this.datasets.get(fileId);
  }

  getAllDatasets() {
    return Array.from(this.datasets.entries()).map(([fileId, info]) => ({
      file_id: fileId,
      filename: info.filename,
      columns: info.columns,
      row_count: info.rowCount,
      file_size_bytes: info.file_size_bytes,
      upload_time: info.upload_time,
      created_at: info.created_at
    }));
  }

  async deleteDataset(fileId) {
    if (!this.datasets.has(fileId)) {
      throw new Error('Dataset not found');
    }

    const dataset = this.datasets.get(fileId);
    
    try {
      await this.conn.query(`DROP TABLE IF EXISTS ${dataset.tableName}`);
      
      this.datasets.delete(fileId);

      try {
        await datasetStorage.deleteDataset(fileId);
      } catch (error) {
        console.warn('Failed to delete persisted dataset:', error);
      }
      
      return { message: `File ${dataset.filename} deleted successfully` };
    } catch (error) {
      console.error('Error deleting dataset:', error);
      throw error;
    }
  }

  async clearAllDatasets() {
    if (!this.initialized) {
      await this.initialize();
    }

    const datasets = Array.from(this.datasets.values());
    for (const dataset of datasets) {
      try {
        await this.conn.query(`DROP TABLE IF EXISTS ${dataset.tableName}`);
      } catch (error) {
        console.warn(`Failed to drop table ${dataset.tableName}:`, error);
      }
    }

    this.datasets.clear();

    try {
      await datasetStorage.clearAll();
    } catch (error) {
      console.warn('Failed to clear persisted datasets:', error);
    }
  }

  async close() {
    if (this.conn) {
      await this.conn.close();
    }
    if (this.db) {
      await this.db.terminate();
    }
    this.initialized = false;
  }

  async convertExcelToCSV(excelBuffer) {
    try {
      // Read the Excel file
      const workbook = XLSX.read(excelBuffer, { type: 'array' });
      
      // Get the first sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Convert to CSV
      const csvContent = XLSX.utils.sheet_to_csv(worksheet, {
        blankrows: false,
        rawNumbers: true
      });
      
      return csvContent;
    } catch (error) {
      console.error('Excel to CSV conversion error:', error);
      throw new Error('Failed to convert Excel file to CSV format');
    }
  }
}

const duckdbService = new DuckDBService();

export default duckdbService;
